import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@schemas'
import { reportActorKey, useCurrentUser, useShowSeries } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { PageHeading } from '@/components/page-heading'

function isAccessError(error: unknown) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status)
}

function SeriesChooser() {
  const query = useShowSeries(undefined, { allOrganizations: true })
  const [denied, setDenied] = useState(false)
  const qc = useQueryClient()
  useEffect(() => {
    if (isAccessError(query.error)) setDenied(true)
  }, [query.error])
  useEffect(() => {
    if (!denied) return
    void qc.cancelQueries({ queryKey: ['show-series'] })
    qc.removeQueries({ queryKey: ['show-series'] })
  }, [denied, qc])

  if (denied || isAccessError(query.error)) {
    return (
      <p role="alert">Series access has changed. Refresh the page to check your current access.</p>
    )
  }
  if (query.isError) {
    return (
      <div role="alert">
        <p>Unable to load show series.</p>
        <Button onClick={() => void query.refetch()}>Retry series</Button>
      </div>
    )
  }
  if (query.isPending) return <p role="status">Loading show series…</p>
  const series = query.data.pages.flatMap(page => page.series)
  return (
    <div className="space-y-4">
      {!series.length && <p>No show series are available with your current access.</p>}
      <ul className="space-y-3">
        {series.map(item => (
          <li key={item.id} className="rounded-lg border p-4 space-y-2">
            <h2 className="font-semibold">{item.name}</h2>
            <p className="text-sm text-muted-foreground">{item.slug}</p>
            <Button asChild>
              <Link to={`/show-series/${item.id}/reports`} aria-label={`Open series ${item.name}`}>
                Open series
              </Link>
            </Button>
          </li>
        ))}
      </ul>
      {query.hasNextPage && (
        <Button disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? 'Loading more…' : 'Load more series'}
        </Button>
      )}
    </div>
  )
}

export default function ShowSeriesPage() {
  const user = useCurrentUser()
  return (
    <div className="space-y-6">
      <PageHeading>Show Series</PageHeading>
      <p>Choose a series to view reports, align classes, and manage its settings.</p>
      {user.data ? (
        <SeriesChooser key={JSON.stringify(reportActorKey(user.data))} />
      ) : (
        <p role="status">Checking series access…</p>
      )}
    </div>
  )
}
